import { cn } from "@/lib/utils";

interface Props {
  /** date (UTC 'YYYY-MM-DD') → value. Missing dates render as empty cells. */
  values: Record<string, number>;
  /** how many days back from today to render (inclusive of today). */
  days: number;
  className?: string;
}

interface Cell {
  date: string | null;
  value: number;
}

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// GitHub/timespent-style cadence grid: weekday rows, week columns, single-hue
// teal intensity ramp. Dependency-free. Reads color from brand tokens, so it
// inherits the kovach-steele palette and stays flat (no shadows).
export function CadenceHeatmap({ values, days, className }: Props) {
  const end = new Date();
  end.setUTCHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setUTCDate(end.getUTCDate() - (days - 1));

  const cells: Cell[] = [];
  const offset = start.getUTCDay(); // 0=Sun … 6=Sat
  for (let i = 0; i < offset; i++) cells.push({ date: null, value: 0 });
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    const key = ymd(d);
    cells.push({ date: key, value: values[key] ?? 0 });
  }
  while (cells.length % 7 !== 0) cells.push({ date: null, value: 0 });

  const max = Math.max(1, ...cells.map((c) => c.value));
  const columns: Cell[][] = [];
  for (let c = 0; c < cells.length / 7; c++) columns.push(cells.slice(c * 7, c * 7 + 7));

  function tone(cell: Cell): string {
    if (cell.date == null) return "bg-transparent";
    if (cell.value <= 0) return "bg-muted";
    const r = cell.value / max;
    if (r <= 0.25) return "bg-primary/25";
    if (r <= 0.5) return "bg-primary/45";
    if (r <= 0.75) return "bg-primary/70";
    return "bg-primary";
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex gap-[3px] overflow-x-auto pb-1">
        {columns.map((col, ci) => (
          <div key={ci} className="flex flex-col gap-[3px]">
            {col.map((cell, ri) => (
              <div
                key={ri}
                title={cell.date ? `${cell.date}: ${cell.value.toLocaleString()}` : undefined}
                className={cn("size-3 rounded-[3px]", tone(cell))}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <span>Less</span>
        <span className="size-2.5 rounded-[2px] bg-muted" />
        <span className="size-2.5 rounded-[2px] bg-primary/25" />
        <span className="size-2.5 rounded-[2px] bg-primary/45" />
        <span className="size-2.5 rounded-[2px] bg-primary/70" />
        <span className="size-2.5 rounded-[2px] bg-primary" />
        <span>More</span>
      </div>
    </div>
  );
}
